import { getRoomTabPath } from '../service/constants';

const NOTIFICATION_WORKER_URL = '/messenger-notifications-sw.js?v=messenger-visible-thread-8';
const NOTIFICATION_TAG_PREFIX = 'messenger-v2:';
export const MESSENGER_V2_CALL_STATE_EVENT = 'nettgrav:messenger-v2:call-state';
export const MESSENGER_V2_INCOMING_CALL_EVENT = 'nettgrav:messenger-v2:incoming-call';
const DEFAULT_NOTIFICATION_ICON = '/favicon-192.png';
const MESSENGER_V2_CALL_SIGNAL_CONTENT_TYPE =
  'application/vnd.nettgrav.messenger-v2.call-signal+mls';

let registrationPromise = null;
let bridgeInstalled = false;

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const canUseMessengerServiceWorker = () =>
  typeof window !== 'undefined' && typeof navigator !== 'undefined' && 'serviceWorker' in navigator;

const canUseDesktopNotifications = () => canUseMessengerServiceWorker() && 'Notification' in window;

const isMessengerV2NotificationTraceEnabled = () => {
  try {
    const win = typeof window !== 'undefined' ? window : null;
    if (!win) return false;
    if (win.__MESSENGER_V2_NOTIFICATION_TRACE__ === true) return true;
    const storedValue =
      win.localStorage?.getItem('MESSENGER_V2_NOTIFICATION_TRACE') ??
      win.sessionStorage?.getItem('MESSENGER_V2_NOTIFICATION_TRACE') ??
      win.localStorage?.getItem('MESSENGER_V2_DEBUG_LOGS') ??
      win.sessionStorage?.getItem('MESSENGER_V2_DEBUG_LOGS');
    return ['1', 'true', 'on', 'yes'].includes(
      String(storedValue || '')
        .trim()
        .toLowerCase(),
    );
  } catch {
    return false;
  }
};

export const traceMessengerV2NotificationEvent = (event = {}) => {
  if (!isMessengerV2NotificationTraceEnabled()) {
    return;
  }

  try {
    const nextEvent = {
      at: new Date().toISOString(),
      ...event,
    };

    if (typeof window !== 'undefined') {
      if (!Array.isArray(window.__MESSENGER_V2_NOTIFICATION_EVENTS__)) {
        window.__MESSENGER_V2_NOTIFICATION_EVENTS__ = [];
      }
      window.__MESSENGER_V2_NOTIFICATION_EVENTS__.push(nextEvent);
      if (window.__MESSENGER_V2_NOTIFICATION_EVENTS__.length > 200) {
        window.__MESSENGER_V2_NOTIFICATION_EVENTS__.splice(
          0,
          window.__MESSENGER_V2_NOTIFICATION_EVENTS__.length - 200,
        );
      }
    }

    // eslint-disable-next-line no-console
    console.info('[MessengerV2NotificationTrace]', nextEvent);
  } catch {}
};

export const getMessengerV2NotificationPermission = () => {
  if (!canUseDesktopNotifications()) {
    return 'unsupported';
  }
  return window.Notification.permission;
};

export const requestMessengerV2NotificationPermission = async () => {
  if (!canUseDesktopNotifications()) {
    return 'unsupported';
  }

  if (window.Notification.permission !== 'default') {
    return window.Notification.permission;
  }

  try {
    return await window.Notification.requestPermission();
  } catch {
    return window.Notification.permission;
  }
};

export const createMessengerV2ConversationNotificationKey = ({
  conversationId = null,
  serverMsgId = null,
  serverSeq = null,
  unreadCount = null,
  previewText = null,
} = {}) => {
  const normalizedMessageId = toStringOrNull(serverMsgId);
  const normalizedServerSeq = serverSeq != null ? String(serverSeq) : null;
  if (normalizedMessageId || normalizedServerSeq) {
    return `${normalizedMessageId || 'no-msg'}:${normalizedServerSeq || 'no-seq'}`;
  }

  return [
    toStringOrNull(conversationId) || 'conversation',
    unreadCount != null ? String(unreadCount) : 'no-count',
    toStringOrNull(previewText) || 'no-preview',
  ].join(':');
};

export const resolveMessengerV2ConversationNotificationUrl = ({
  conversation = null,
  conversationId = null,
} = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId ?? conversation?.id ?? null);
  const kind = String(conversation?.kind || '')
    .trim()
    .toLowerCase();
  const roomId = toStringOrNull(
    conversation?.room_id ?? conversation?.roomId ?? conversation?.room?.id ?? null,
  );

  if (kind === 'room' && roomId) {
    return getRoomTabPath(encodeURIComponent(roomId), 'members_chat');
  }

  return normalizedConversationId
    ? `/messages/${encodeURIComponent(normalizedConversationId)}`
    : '/messages';
};

export const isMessengerV2ConversationNotificationMuted = (conversation = null) => {
  const kind = String(conversation?.kind || '')
    .trim()
    .toLowerCase();
  if (kind !== 'room') return false;

  return (
    conversation?.room?.notifications_muted === true ||
    conversation?.room?.notificationsMuted === true ||
    conversation?.notifications_muted === true ||
    conversation?.notificationsMuted === true
  );
};

export const createMessengerV2InitialsAvatarDataUrl = ({ name = null, initials = null } = {}) => {
  const suppliedInitials = toStringOrNull(initials);
  const words = String(name || '').match(/[\p{L}\p{N}]+/gu) || [];
  const label = (
    suppliedInitials ||
    words
      .slice(0, 2)
      .map((word) => word[0])
      .join('') ||
    'N'
  )
    .slice(0, 2)
    .toUpperCase();
  const safeLabel = label.replace(
    /[&<>"']/g,
    (character) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' })[character],
  );
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96"><rect width="96" height="96" rx="24" fill="#0ea5e9"/><text x="48" y="53" fill="#ffffff" font-family="Arial,sans-serif" font-size="34" font-weight="700" text-anchor="middle" dominant-baseline="middle">${safeLabel}</text></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

export const ensureMessengerV2NotificationServiceWorker = async () => {
  if (!canUseMessengerServiceWorker()) {
    return null;
  }

  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker
      .register(NOTIFICATION_WORKER_URL, { scope: '/', updateViaCache: 'none' })
      .then((registration) => {
        void registration.update?.().catch(() => null);
        return registration;
      })
      .catch((error) => {
        registrationPromise = null;
        throw error;
      });
  }

  return registrationPromise;
};

export const installMessengerV2NotificationBridge = ({ onOpenConversation } = {}) => {
  if (!canUseMessengerServiceWorker() || bridgeInstalled) {
    return () => {};
  }

  const handleMessage = (event) => {
    const payload = event?.data;
    if (!payload || typeof payload !== 'object') {
      return;
    }

    if (payload.type === 'MESSENGER_V2_CALL_STATE') {
      window.dispatchEvent(
        new CustomEvent(MESSENGER_V2_CALL_STATE_EVENT, {
          detail: payload,
        }),
      );
      return;
    }

    if (payload.type === 'MESSENGER_V2_INCOMING_CALL') {
      window.dispatchEvent(
        new CustomEvent(MESSENGER_V2_INCOMING_CALL_EVENT, {
          detail: payload,
        }),
      );
      onOpenConversation?.({
        conversationId: toStringOrNull(payload.conversationId ?? null),
        callId: toStringOrNull(payload.callId ?? null),
        url: toStringOrNull(payload.url ?? null),
      });
      return;
    }

    if (payload.type !== 'MESSENGER_V2_OPEN_CONVERSATION') {
      return;
    }

    onOpenConversation?.({
      conversationId: toStringOrNull(payload.conversationId ?? null),
      callId: toStringOrNull(payload.callId ?? null),
      url: toStringOrNull(payload.url ?? null),
    });
  };

  navigator.serviceWorker.addEventListener('message', handleMessage);
  bridgeInstalled = true;

  return () => {
    navigator.serviceWorker.removeEventListener('message', handleMessage);
    bridgeInstalled = false;
  };
};

export const createMessengerV2NotificationPayload = ({
  body = 'New message',
  conversationId = null,
  conversationUrl = null,
  frontierKey = null,
  messageId = null,
  icon = null,
  senderName = null,
  senderInitials = null,
  badge = null,
  renotify = true,
  requireInteraction = false,
  silent = true,
} = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  return {
    body: body || 'New message',
    tag: `${NOTIFICATION_TAG_PREFIX}${normalizedConversationId}:${toStringOrNull(frontierKey ?? messageId ?? normalizedConversationId)}`,
    renotify,
    silent,
    requireInteraction,
    icon:
      toStringOrNull(icon) ||
      createMessengerV2InitialsAvatarDataUrl({ name: senderName, initials: senderInitials }),
    badge: toStringOrNull(badge) || DEFAULT_NOTIFICATION_ICON,
    data: {
      type: 'MESSENGER_V2_OPEN_CONVERSATION',
      conversationId: normalizedConversationId,
      url: conversationUrl || `/messages/${normalizedConversationId}`,
      frontierKey: toStringOrNull(frontierKey ?? null),
      messageId: toStringOrNull(messageId ?? null),
    },
  };
};

export const showMessengerV2ConversationNotification = async ({
  title = 'New message',
  body = 'New message',
  conversationId = null,
  conversationUrl = null,
  frontierKey = null,
  messageId = null,
  icon = null,
  senderName = null,
  senderInitials = null,
  badge = null,
  contentType = null,
  signalType = null,
  requireInteraction = false,
  renotify = true,
  silent = true,
} = {}) => {
  const registration = await ensureMessengerV2NotificationServiceWorker().catch(() => null);
  const normalizedConversationId = toStringOrNull(conversationId);
  const normalizedContentType = toStringOrNull(contentType)?.toLowerCase();
  const normalizedSignalType = toStringOrNull(signalType)?.toLowerCase();
  const isSilentCallSignal =
    normalizedSignalType && normalizedSignalType !== 'offer' && normalizedSignalType !== 'invite';
  if (
    isSilentCallSignal ||
    (normalizedContentType === MESSENGER_V2_CALL_SIGNAL_CONTENT_TYPE &&
      normalizedSignalType !== 'offer' &&
      normalizedSignalType !== 'invite')
  ) {
    traceMessengerV2NotificationEvent({
      stage: 'show:skipped',
      reason: 'silent_call_signal',
      conversationId: normalizedConversationId,
      signalType: normalizedSignalType,
    });
    return null;
  }
  if (!normalizedConversationId) {
    traceMessengerV2NotificationEvent({
      stage: 'show:skipped',
      reason: 'missing_conversation_id',
      title,
    });
    return null;
  }

  const notificationPayload = createMessengerV2NotificationPayload({
    body,
    conversationId: normalizedConversationId,
    conversationUrl,
    frontierKey,
    messageId,
    icon,
    senderName,
    senderInitials,
    badge,
    renotify,
    silent,
    requireInteraction,
  });

  try {
    if (registration?.showNotification) {
      traceMessengerV2NotificationEvent({
        stage: 'show:service-worker',
        conversationId: normalizedConversationId,
        title,
        hasRegistration: true,
      });
      return await registration.showNotification(title || 'New message', notificationPayload);
    }
  } catch {
    traceMessengerV2NotificationEvent({
      stage: 'show:service-worker-error',
      conversationId: normalizedConversationId,
      title,
    });
    // Fall through to the native Notification fallback below.
  }

  if (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    window.Notification.permission === 'granted'
  ) {
    traceMessengerV2NotificationEvent({
      stage: 'show:native',
      conversationId: normalizedConversationId,
      title,
    });
    const nativeNotification = new window.Notification(title || 'New message', notificationPayload);

    nativeNotification.onclick = () => {
      try {
        window.focus?.();
        const nextUrl = notificationPayload.data?.url || `/messages/${normalizedConversationId}`;
        window.location.assign(nextUrl);
      } catch {}
      try {
        nativeNotification.close();
      } catch {}
    };

    return nativeNotification;
  }

  traceMessengerV2NotificationEvent({
    stage: 'show:skipped',
    reason: 'no_supported_notification_path',
    conversationId: normalizedConversationId,
    title,
  });
  return null;
};

export const closeMessengerV2ConversationNotifications = async ({
  conversationId = null,
  frontierKey = null,
  messageId = null,
} = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId) return;

  const registration = await ensureMessengerV2NotificationServiceWorker().catch(() => null);

  try {
    if (registration?.getNotifications) {
      const normalizedFrontier = toStringOrNull(frontierKey ?? messageId ?? null);
      const notifications = normalizedFrontier
        ? await registration.getNotifications({
            tag: `${NOTIFICATION_TAG_PREFIX}${normalizedConversationId}:${normalizedFrontier}`,
          })
        : await registration.getNotifications();
      notifications
        .filter(
          (notification) =>
            normalizedFrontier ||
            (String(notification?.tag || '').startsWith(NOTIFICATION_TAG_PREFIX) &&
              toStringOrNull(notification?.data?.conversationId ?? null) ===
                normalizedConversationId),
        )
        .forEach((notification) => {
          try {
            notification.close();
          } catch {}
        });
      return;
    }
  } catch {}
};

export const resolveMessengerV2NotificationVisibility = () => {
  if (typeof document === 'undefined') {
    return 'visible';
  }

  const visibilityState = document.visibilityState || 'visible';
  if (visibilityState !== 'visible') {
    return 'hidden';
  }

  return 'visible';
};

export const shouldShowMessengerV2ConversationNotification = ({
  permission = null,
  visibilityState = 'visible',
  conversationId = null,
  frontierKey = null,
  lastNotifiedFrontierKey = null,
  conversationVisibleAnywhere = false,
} = {}) => {
  const shouldShow =
    permission === 'granted' &&
    visibilityState !== 'visible' &&
    conversationVisibleAnywhere !== true &&
    !!toStringOrNull(conversationId) &&
    !!toStringOrNull(frontierKey) &&
    toStringOrNull(frontierKey) !== toStringOrNull(lastNotifiedFrontierKey);

  traceMessengerV2NotificationEvent({
    stage: 'gate',
    permission,
    visibilityState,
    conversationId: toStringOrNull(conversationId),
    frontierKey: toStringOrNull(frontierKey),
    lastNotifiedFrontierKey: toStringOrNull(lastNotifiedFrontierKey),
    conversationVisibleAnywhere: conversationVisibleAnywhere === true,
    shouldShow,
  });

  return shouldShow;
};

export const resolveMessengerV2NotificationBody = ({
  preview = null,
  fallbackLabel = 'New message',
} = {}) => {
  const normalizedPreview = toStringOrNull(preview);
  if (!normalizedPreview) {
    return fallbackLabel;
  }
  if (normalizedPreview.length <= 80) {
    return normalizedPreview;
  }
  return `${normalizedPreview.slice(0, 80).trimEnd()}...`;
};
